import { useState } from 'react';
import {
  Tabs,
  Form,
  Input,
  Button,
  Typography,
  Descriptions,
  Spin,
  message,
  Card,
} from 'antd';
import {
  UserOutlined,
  LockOutlined,
  InfoCircleOutlined,
} from '@ant-design/icons';
import { useProfile, useUpdateProfile, useChangePassword } from '@hooks/useSettings';
import type { UpdateProfileRequest, ChangePasswordRequest } from '@/types/settings';

function ProfileTab() {
  const { data: mdProfile, isLoading: mdIsLoading } = useProfile();
  const mdUpdateProfile = useUpdateProfile();
  const [mdForm] = Form.useForm();

  const mdHandleSave = async () => {
    const mdValues = await mdForm.validateFields();
    const mdRequest: UpdateProfileRequest = {
      firstName: mdValues.firstName,
      lastName: mdValues.lastName,
      email: mdValues.email,
    };
    await mdUpdateProfile.mutateAsync(mdRequest);
    message.success('Профіль оновлено');
  };

  if (mdIsLoading) {
    return (
      <div style={{ textAlign: 'center', padding: 48 }}>
        <Spin size="large" />
      </div>
    );
  }

  return (
    <Card style={{ maxWidth: 600 }}>
      <Form
        form={mdForm}
        layout="vertical"
        initialValues={{
          firstName: mdProfile?.firstName ?? '',
          lastName: mdProfile?.lastName ?? '',
          email: mdProfile?.email ?? '',
        }}
      >
        <Form.Item
          name="firstName"
          label="Ім'я"
          rules={[{ required: true, message: "Введіть ім'я" }]}
        >
          <Input maxLength={100} />
        </Form.Item>
        <Form.Item
          name="lastName"
          label="Прізвище"
          rules={[{ required: true, message: 'Введіть прізвище' }]}
        >
          <Input maxLength={100} />
        </Form.Item>
        <Form.Item
          name="email"
          label="Email"
          rules={[
            { required: true, message: 'Введіть email' },
            { type: 'email', message: 'Невалідний email' },
          ]}
        >
          <Input maxLength={200} />
        </Form.Item>
        <Form.Item>
          <Button
            type="primary"
            onClick={mdHandleSave}
            loading={mdUpdateProfile.isPending}
          >
            Зберегти
          </Button>
        </Form.Item>
      </Form>
    </Card>
  );
}

function PasswordTab() {
  const mdChangePassword = useChangePassword();
  const [mdForm] = Form.useForm();

  const mdHandleChangePassword = async () => {
    const mdValues = await mdForm.validateFields();
    const mdRequest: ChangePasswordRequest = {
      currentPassword: mdValues.currentPassword,
      newPassword: mdValues.newPassword,
    };
    await mdChangePassword.mutateAsync(mdRequest);
    message.success('Пароль змінено');
    mdForm.resetFields();
  };

  return (
    <Card style={{ maxWidth: 600 }}>
      <Form form={mdForm} layout="vertical">
        <Form.Item
          name="currentPassword"
          label="Поточний пароль"
          rules={[{ required: true, message: 'Введіть поточний пароль' }]}
        >
          <Input.Password />
        </Form.Item>
        <Form.Item
          name="newPassword"
          label="Новий пароль"
          rules={[
            { required: true, message: 'Введіть новий пароль' },
            { min: 8, message: 'Мінімум 8 символів' },
          ]}
        >
          <Input.Password />
        </Form.Item>
        <Form.Item
          name="confirmPassword"
          label="Підтвердження пароля"
          dependencies={['newPassword']}
          rules={[
            { required: true, message: 'Підтвердіть пароль' },
            ({ getFieldValue }) => ({
              validator(_, mdValue) {
                if (!mdValue || getFieldValue('newPassword') === mdValue) {
                  return Promise.resolve();
                }
                return Promise.reject(new Error('Паролі не збігаються'));
              },
            }),
          ]}
        >
          <Input.Password />
        </Form.Item>
        <Form.Item>
          <Button
            type="primary"
            onClick={mdHandleChangePassword}
            loading={mdChangePassword.isPending}
          >
            Змінити пароль
          </Button>
        </Form.Item>
      </Form>
    </Card>
  );
}

function AboutTab() {
  return (
    <Card style={{ maxWidth: 600 }}>
      <Descriptions bordered column={1} size="small">
        <Descriptions.Item label="Система">MedDevice ERP</Descriptions.Item>
        <Descriptions.Item label="Версія">{__APP_VERSION__}</Descriptions.Item>
        <Descriptions.Item label="Дата збірки">{__APP_BUILD_DATE__}</Descriptions.Item>
        <Descriptions.Item label="Платформа">Electron + React</Descriptions.Item>
        <Descriptions.Item label="Backend">ASP.NET Core 8</Descriptions.Item>
        <Descriptions.Item label="База даних">PostgreSQL 16</Descriptions.Item>
      </Descriptions>
    </Card>
  );
}

export default function SettingsPage() {
  const [mdActiveTab, setMdActiveTab] = useState('profile');

  const mdTabItems = [
    {
      key: 'profile',
      label: (
        <span>
          <UserOutlined /> Профіль
        </span>
      ),
      children: <ProfileTab />,
    },
    {
      key: 'password',
      label: (
        <span>
          <LockOutlined /> Зміна пароля
        </span>
      ),
      children: <PasswordTab />,
    },
    {
      key: 'about',
      label: (
        <span>
          <InfoCircleOutlined /> Про систему
        </span>
      ),
      children: <AboutTab />,
    },
  ];

  return (
    <>
      <Typography.Title level={4} style={{ marginBottom: 16 }}>
        Налаштування
      </Typography.Title>
      <Tabs
        activeKey={mdActiveTab}
        onChange={setMdActiveTab}
        items={mdTabItems}
      />
    </>
  );
}
